import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { FilePenLine, LoaderCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ADMIN_CPM_CAMPAIGN_CONTENT, ADMIN_UPDATE_CPM_CAMPAIGN_CONTENT, type CampaignAuditContent } from '@/queries/campaignAuditContent';
import { buildCampaignContentInput, CAMPAIGN_CONTENT_FIELDS, PERIOD_CONTENT_FIELDS, type CampaignContentDraft, type CampaignContentField } from '@/lib/campaigns/campaignAuditContent';
import { useCampaignAuditContentCopy } from '@/i18n/campaignAuditContent';
import { CampaignMarkdown } from './CampaignMarkdown';

type Props = { campaignId: string; onSaved?: () => unknown | Promise<unknown> };
const asDraft = (content: CampaignAuditContent): CampaignContentDraft => Object.fromEntries(
  CAMPAIGN_CONTENT_FIELDS.map(field => [field, content[field] ?? '']),
);

export function CampaignContentEditor(props: Props) {
  const { user, sessionVerified } = useAuth();
  if (!user || !sessionVerified || !props.campaignId) return null;
  // Remount only this editor across accounts/campaigns, never the surrounding app.
  return <AuthorizedCampaignContentEditor key={`${user.id}:${props.campaignId}`} {...props} />;
}

function AuthorizedCampaignContentEditor({ campaignId, onSaved }: Props) {
  const client = useApolloClient();
  const copy = useCampaignAuditContentCopy();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState<CampaignAuditContent | null>(null);
  const [draft, setDraft] = useState<CampaignContentDraft>({});
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);
  const revision = useRef(0);
  const openRef = useRef(false);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; revision.current += 1; }; }, []);
  const current = (expected: number) => mounted.current && openRef.current && revision.current === expected;

  const load = async () => {
    const expected = ++revision.current;
    setLoading(true); setContent(null); setDraft({}); setError(null);
    try {
      const result = await client.query({
        query: ADMIN_CPM_CAMPAIGN_CONTENT, variables: { campaignId },
        fetchPolicy: 'no-cache', errorPolicy: 'all', context: { queryDeduplication: false },
      });
      if (!current(expected)) return;
      const next = result.data?.adminCpmCampaignContent;
      if (result.errors?.length || ('error' in result && result.error) || !next || next.id !== campaignId) throw new Error('campaign_content_failed');
      setContent(next); setDraft(asDraft(next));
    } catch {
      if (current(expected)) setError(copy.loadError);
    } finally {
      if (current(expected)) setLoading(false);
    }
  };
  const changeOpen = (next: boolean) => {
    openRef.current = next;
    setOpen(next);
    if (next) void load();
    else { revision.current += 1; setContent(null); setDraft({}); setError(null); setLoading(false); setSaving(false); }
  };
  const changes = useMemo(() => {
    if (!content) return null;
    try { return buildCampaignContentInput(content, draft); } catch { return null; }
  }, [content, draft]);
  const finalized = content?.editing_locked_reason === 'ranking_finalized';
  const readOnly = finalized || (!!content?.content_locked && content.editing_locked_reason !== 'ranking_period_locked');
  const update = (field: CampaignContentField, value: string) => setDraft(previous => ({ ...previous, [field]: value }));
  const disabled = (field: CampaignContentField) => saving || readOnly || (!!content?.content_locked && PERIOD_CONTENT_FIELDS.includes(field));
  const save = async () => {
    if (!content || saving || readOnly || !changes || !Object.keys(changes).length) return;
    const expected = revision.current;
    setSaving(true); setError(null);
    try {
      const result = await client.mutate({
        mutation: ADMIN_UPDATE_CPM_CAMPAIGN_CONTENT,
        variables: { campaignId, input: changes }, fetchPolicy: 'no-cache', errorPolicy: 'all',
      });
      if (!current(expected)) return;
      if (result.errors?.length || ('error' in result && result.error) || result.data?.adminUpdateCpmCampaignContent?.id !== campaignId) {
        throw new Error(result.errors?.map(item => item.message).join(' ') || 'campaign_content_failed');
      }
      changeOpen(false);
      // A successful write stays successful even if refreshing the parent fails.
      try { void Promise.resolve(onSaved?.()).catch(() => undefined); } catch { /* Parent retry controls handle refresh errors. */ }
    } catch (cause) {
      if (!current(expected)) return;
      const message = cause instanceof Error ? cause.message : '';
      if (message.includes('ranking_period_locked') || message.includes('ranking_finalized')) {
        const reason = message.includes('ranking_finalized') ? 'ranking_finalized' : 'ranking_period_locked';
        setContent(previous => previous ? { ...previous, content_locked: true, editing_locked_reason: reason } : previous);
        setError(reason === 'ranking_finalized' ? copy.finalized : copy.periodLocked);
      } else if (/unauthori[sz]ed|unauthenticated|forbidden/i.test(message)) {
        setContent(null); setDraft({}); setError(copy.loadError);
      } else setError(copy.saveError);
    } finally { if (current(expected)) setSaving(false); }
  };
  const markdownField = (field: CampaignContentField, label: string, rows = 4) => <div className="space-y-2" key={field}>
    <Label htmlFor={`campaign-content-${field}`}>{label}</Label>
    <Textarea id={`campaign-content-${field}`} value={draft[field] || ''} rows={rows} maxLength={65_536} disabled={disabled(field)} onChange={event => update(field, event.target.value)} className="font-mono text-sm" />
    {!!draft[field]?.trim() && <details className="rounded-md border p-3"><summary className="cursor-pointer text-sm font-medium">{copy.preview}</summary><CampaignMarkdown className="mt-3">{draft[field] || ''}</CampaignMarkdown></details>}
  </div>;

  return <>
    <Button variant="outline" size="sm" onClick={() => changeOpen(true)}><FilePenLine className="mr-2 h-4 w-4" aria-hidden="true" />{copy.edit}</Button>
    <Dialog open={open} onOpenChange={next => { if (!saving) changeOpen(next); }}>
      <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col overflow-hidden">
        <DialogHeader><DialogTitle>{copy.title}</DialogTitle><DialogDescription>{copy.description}</DialogDescription></DialogHeader>
        <div className="min-h-0 space-y-4 overflow-y-auto px-1">
          {loading && <p aria-busy="true" className="flex items-center gap-2 text-sm"><LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />{copy.loading}</p>}
          {error && <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">{error}</p>}
          {!loading && !content && <Button variant="outline" onClick={() => void load()}>{copy.retry}</Button>}
          {content && <>
            {!content.content_locked && !changes && <p role="alert" className="text-sm text-destructive">{copy.invalid}</p>}
            {content.content_locked && <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">{finalized ? copy.finalized : copy.periodLocked}</p>}
            {content.scheduled_activation_at && <p className="text-sm text-muted-foreground">{copy.opens}: {new Date(content.scheduled_activation_at).toLocaleString(undefined, { timeZone: 'America/Sao_Paulo' })} (Brasília)</p>}
            <Tabs defaultValue="information">
              <TabsList className="grid w-full grid-cols-3"><TabsTrigger value="information">{copy.information}</TabsTrigger><TabsTrigger value="rules">{copy.rules}</TabsTrigger><TabsTrigger value="artwork">{copy.artwork}</TabsTrigger></TabsList>
              <TabsContent value="information" className="space-y-4">
                <div className="space-y-2"><Label htmlFor="campaign-content-name">{copy.name}</Label><Input id="campaign-content-name" value={draft.name || ''} maxLength={255} disabled={disabled('name')} onChange={event => update('name', event.target.value)} /></div>
                <p className="text-xs text-muted-foreground">{copy.markdown}</p>
                {markdownField('description', copy.descriptionField, 8)}
              </TabsContent>
              <TabsContent value="rules" className="space-y-4">
                <p className="text-xs text-muted-foreground">{copy.markdown}</p>
                {(['rules_allowed', 'rules_forbidden', 'rules_attention', 'rules_warning'] as const).map(field => markdownField(field, copy[field]))}
                {(['required_hashtags', 'required_mention'] as const).map(field => <div className="space-y-2" key={field}><Label htmlFor={`campaign-content-${field}`}>{copy[field]}</Label><Textarea id={`campaign-content-${field}`} value={draft[field] || ''} disabled={disabled(field)} onChange={event => update(field, event.target.value)} /></div>)}
              </TabsContent>
              <TabsContent value="artwork" className="space-y-4">
                {(['banner_url', 'logo_url'] as const).map(field => <div className="space-y-2" key={field}><Label htmlFor={`campaign-content-${field}`}>{copy[field]}</Label><Input id={`campaign-content-${field}`} type="url" maxLength={2048} placeholder="https://" value={draft[field] || ''} disabled={disabled(field)} onChange={event => update(field, event.target.value)} /></div>)}
              </TabsContent>
            </Tabs>
          </>}
        </div>
        <DialogFooter className="shrink-0 border-t pt-4"><Button variant="outline" disabled={saving} onClick={() => changeOpen(false)}>{copy.cancel}</Button><Button disabled={loading || saving || readOnly || !changes || !Object.keys(changes).length} onClick={() => void save()}>{saving && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}{saving ? copy.saving : copy.save}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
